import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useCrmOrgStore } from '../../store/crmOrg.store';
import DetailPageLayout from '../../components/crm/DetailPageLayout';
import contactsService, { Contact } from '../../services/crm/contactsService';
import companiesService, { Company } from '../../services/crm/companiesService';
import activitiesService, { Activity } from '../../services/crm/activitiesService';
import leadScoringService, { LeadScore, ScoringRule } from '../../services/crm/leadScoringService';
import tagsService from '../../services/crm/tagsService';
import workflowEnrollmentsService from '../../services/crm/workflowEnrollmentsService';
import duplicatesService from '../../services/crm/duplicatesService';

// Import CRM Components
import LeadScoreCard from '../../components/crm/LeadScoreCard';
import ActivityTimeline from '../../components/crm/ActivityTimeline';
import UnifiedActivityTimeline from '../../components/crm/UnifiedActivityTimeline';
import QuickActionsBar from '../../components/crm/QuickActionsBar';
import SmartInsightsPanel from '../../components/crm/SmartInsightsPanel';
import NotesPanel from '../../components/crm/NotesPanel';
import TagsManager from '../../components/crm/TagsManager';
import WorkflowEnrollmentStatus from '../../components/crm/WorkflowEnrollmentStatus';
import AutomationManager from '../../components/crm/AutomationManager';
import DuplicateAlert from '../../components/crm/DuplicateAlert';
import EmailCampaignWidget from '../../components/crm/EmailCampaignWidget';

const personaOptions = [
  { value: '', label: 'No persona' },
  { value: 'economic-buyer', label: 'Economic buyer' },
  { value: 'champion', label: 'Champion' },
  { value: 'technical-evaluator', label: 'Technical evaluator' },
  { value: 'influencer', label: 'Influencer' }
];

const lifecycleStageOptions = [
  { value: '', label: 'No lifecycle stage' },
  { value: 'lead', label: 'Lead' },
  { value: 'mql', label: 'Marketing qualified' },
  { value: 'sql', label: 'Sales qualified' },
  { value: 'opportunity', label: 'Opportunity' },
  { value: 'customer', label: 'Customer' }
];

const leadStatusOptions = [
  { value: '', label: 'No lead status' },
  { value: 'new', label: 'New' },
  { value: 'working', label: 'Working' },
  { value: 'nurturing', label: 'Nurturing' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'unqualified', label: 'Unqualified' }
];

const ContactDetailEnhanced: React.FC = () => {
  const { contactId = '' } = useParams();
  const navigate = useNavigate();
  const { activeOrgId } = useCrmOrgStore();

  // Contact State
  const [contact, setContact] = useState<Contact | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form State - Enterprise Fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState(''); // Legacy
  const [mobilePhone, setMobilePhone] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [businessPhoneExt, setBusinessPhoneExt] = useState('');
  const [homePhone, setHomePhone] = useState('');
  const [otherPhone, setOtherPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [department, setDepartment] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [persona, setPersona] = useState('');
  const [lifecycleStage, setLifecycleStage] = useState('');
  const [leadStatus, setLeadStatus] = useState('');
  const [leadSource, setLeadSource] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [addressStreet, setAddressStreet] = useState('');
  const [addressCity, setAddressCity] = useState('');
  const [addressState, setAddressState] = useState('');
  const [addressPostalCode, setAddressPostalCode] = useState('');
  const [addressCountry, setAddressCountry] = useState('');
  const [notes, setNotes] = useState('');
  const [region, setRegion] = useState('');

  // Enhanced CRM Features State
  const [leadScore, setLeadScore] = useState<LeadScore | null>(null);
  const [scoreBreakdown, setScoreBreakdown] = useState<ScoringRule[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [tags, setTags] = useState<any[]>([]);
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [workflowEnrollments, setWorkflowEnrollments] = useState<any[]>([]);
  const [sequenceEnrollments, setSequenceEnrollments] = useState<any[]>([]);
  const [duplicates, setDuplicates] = useState<any[]>([]);
  const [insights, setInsights] = useState<any[]>([]);

  useEffect(() => { document.title = 'CRM | Contact Details'; }, []);

  // Load companies
  useEffect(() => {
    const fetchCompanies = async () => {
      if (!activeOrgId) {
        setCompanies([]);
        return;
      }
      try {
        const resp = await companiesService.list({ organization_id: activeOrgId, limit: 100, offset: 0 });
        setCompanies(resp.data || []);
      } catch {
        setCompanies([]);
      }
    };
    void fetchCompanies();
  }, [activeOrgId]);

  // Generate smart insights
  const generateInsights = useCallback((contactData: Contact, score: LeadScore | null) => {
    const newInsights: any[] = [];

    if (score && score.score >= 70) {
      newInsights.push({
        id: 'high-score',
        type: 'success',
        title: 'Hot Lead Detected',
        message: `This contact has a lead score of ${score.score}. Consider prioritizing outreach.`
      });
    }

    if (score && score.score < 30) {
      newInsights.push({
        id: 'low-score',
        type: 'warning',
        title: 'Low Engagement',
        message: 'This contact has low engagement. Consider nurture campaigns.'
      });
    }

    const missingFields = [];
    if (!contactData.email) missingFields.push('email');
    if (!contactData.phone) missingFields.push('phone');
    if (!contactData.company_id) missingFields.push('company');
    if (missingFields.length > 0) {
      newInsights.push({
        id: 'missing-data',
        type: 'info',
        title: 'Incomplete Profile',
        message: `Missing: ${missingFields.join(', ')}. Complete the profile for better targeting.`
      });
    }

    if (activities.length === 0) {
      newInsights.push({
        id: 'no-activity',
        type: 'urgent',
        title: 'No Recent Activity',
        message: 'No activities logged for this contact. Schedule a follow-up.'
      });
    }

    setInsights(newInsights);
  }, [activities.length]);

  // Load contact and all related data
  const loadContactData = useCallback(async () => {
    if (!activeOrgId || !contactId) return;
    setLoading(true);
    setError(null);
    
    try {
      const { data } = await contactsService.get(contactId, activeOrgId);
      if (!data) {
        setError('Contact not found');
        return;
      }
      setContact(data);
      setFirstName(data.first_name || '');
      setLastName(data.last_name || '');
      setEmail(data.email || '');
      setPhone(data.phone || '');
      setMobilePhone(data.mobile_phone || '');
      setBusinessPhone(data.business_phone || '');
      setBusinessPhoneExt(data.business_phone_ext || '');
      setHomePhone(data.home_phone || '');
      setOtherPhone(data.other_phone || '');
      setJobTitle(data.job_title || '');
      setDepartment(data.department || '');
      setCompanyId((data.company_id as string) || '');
      setPersona(data.persona || '');
      setLifecycleStage(data.lifecycle_stage || '');
      setLeadStatus(data.lead_status || '');
      setLeadSource(data.lead_source || '');
      setLinkedinUrl(data.linkedin_url || '');
      setAddressStreet(data.address_street || '');
      setAddressCity(data.address_city || '');
      setAddressState(data.address_state || '');
      setAddressPostalCode(data.address_postal_code || '');
      setAddressCountry(data.address_country || '');
      setNotes(data.notes || '');
      setRegion(data.region || '');

      // Load all enhanced features in parallel
      const [scoreResult, activitiesResult, tagsResult, allTagsResult, enrollmentsResult, duplicatesResult] = await Promise.allSettled([
        leadScoringService.getScore(activeOrgId, 'contact', contactId),
        activitiesService.list({ organization_id: activeOrgId, entity_type: 'contact', entity_id: contactId, limit: 20 }),
        tagsService.getEntityTags(activeOrgId, 'contact', contactId),
        tagsService.list(activeOrgId),
        workflowEnrollmentsService.getEnrollments(activeOrgId, 'contact', contactId),
        duplicatesService.findDuplicates(activeOrgId, 'contact', contactId)
      ]);

      // Handle lead score
      if (scoreResult.status === 'fulfilled' && scoreResult.value) {
        setLeadScore(scoreResult.value);
        const breakdown = await leadScoringService.getScoreBreakdown(activeOrgId, 'contact', contactId);
        setScoreBreakdown(breakdown);
      }

      // Handle activities
      if (activitiesResult.status === 'fulfilled') {
        setActivities(activitiesResult.value.data || []);
      }

      // Handle tags
      if (tagsResult.status === 'fulfilled') {
        setTags(tagsResult.value.data || []);
      }
      if (allTagsResult.status === 'fulfilled') {
        setAvailableTags(allTagsResult.value.data || []);
      }

      // Handle workflow enrollments
      if (enrollmentsResult.status === 'fulfilled') {
        setWorkflowEnrollments(enrollmentsResult.value.workflows || []);
        setSequenceEnrollments(enrollmentsResult.value.sequences || []);
      }

      // Handle duplicates
      if (duplicatesResult.status === 'fulfilled') {
        setDuplicates(duplicatesResult.value.data || []);
      }

      // Generate insights
      generateInsights(data, scoreResult.status === 'fulfilled' ? scoreResult.value : null);

    } catch (e: any) {
      setError(e?.message || 'Failed to load contact');
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, contactId, generateInsights]);

  useEffect(() => { void loadContactData(); }, [loadContactData]);

  // Computed values
  const assignedCompany = useMemo(() => companies.find((company) => company.id === companyId) || null, [companies, companyId]);
  const fullName = useMemo(() => [firstName, lastName].filter(Boolean).join(' '), [firstName, lastName]);

  // Save contact
  const save = async () => {
    if (!activeOrgId || !contactId) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        organization_id: activeOrgId,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        email: email || undefined,
        phone: phone || undefined,
        mobile_phone: mobilePhone || undefined,
        business_phone: businessPhone || undefined,
        business_phone_ext: businessPhoneExt || undefined,
        home_phone: homePhone || undefined,
        other_phone: otherPhone || undefined,
        job_title: jobTitle || undefined,
        department: department || undefined,
        company_id: companyId || undefined,
        persona: persona || undefined,
        lifecycle_stage: lifecycleStage || undefined,
        lead_status: leadStatus || undefined,
        lead_source: leadSource || undefined,
        linkedin_url: linkedinUrl || undefined,
        address_street: addressStreet || undefined,
        address_city: addressCity || undefined,
        address_state: addressState || undefined,
        address_postal_code: addressPostalCode || undefined,
        address_country: addressCountry || undefined,
        notes: notes || undefined,
        region: region || undefined
      };
      const { data } = await contactsService.update(contactId, payload);
      setContact(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  // Tag handlers
  const handleAddTag = async (tagId: string) => {
    if (!activeOrgId) return;
    await tagsService.addTagToEntity(activeOrgId, 'contact', contactId, tagId);
    await loadContactData();
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!activeOrgId) return;
    await tagsService.removeTagFromEntity(activeOrgId, 'contact', contactId, tagId);
    await loadContactData();
  };

  const handleCreateTag = async (name: string, color: string) => {
    if (!activeOrgId) return { id: '', name, color };
    const result = await tagsService.create({ organization_id: activeOrgId, name, color });
    await loadContactData();
    return result.data;
  };

  // Workflow handlers
  const handlePauseWorkflow = async (enrollmentId: string) => {
    if (!activeOrgId) return;
    await workflowEnrollmentsService.pauseWorkflow(enrollmentId, activeOrgId);
    await loadContactData();
  };

  const handleResumeWorkflow = async (enrollmentId: string) => {
    if (!activeOrgId) return;
    await workflowEnrollmentsService.resumeWorkflow(enrollmentId, activeOrgId);
    await loadContactData();
  };

  const handleUnenrollSequence = async (enrollmentId: string) => {
    if (!activeOrgId) return;
    await workflowEnrollmentsService.unenrollSequence(enrollmentId, activeOrgId);
    await loadContactData();
  };

  // Activity timeline data
  const timelineEvents = useMemo(() => {
    return activities.map(activity => ({
      id: activity.id,
      type: activity.type,
      title: activity.type.charAt(0).toUpperCase() + activity.type.slice(1),
      description: activity.content || '',
      timestamp: activity.created_at,
      metadata: { user: activity.owner_user_id, completed: !!activity.completed_at }
    }));
  }, [activities]);

  // Quick actions for bar
  const quickActionsBarActions = useMemo(() => {
    const actions = [];
    
    if (contact?.email) {
      actions.push({
        id: 'email',
        label: 'Email',
        icon: '✉️',
        onClick: () => window.location.href = `mailto:${contact.email}`,
        variant: 'primary' as const
      });
    }

    actions.push(
      { id: 'call', label: 'Call', icon: '📞', onClick: () => alert('Call functionality coming soon'), variant: 'primary' as const },
      { id: 'task', label: 'Create Task', icon: '✓', onClick: () => navigate(`/crm/activities?contactId=${contactId}&type=task`), variant: 'secondary' as const },
      { id: 'note', label: 'Add Note', icon: '📝', onClick: () => navigate(`/crm/activities?contactId=${contactId}&type=note`), variant: 'secondary' as const }
    );

    return actions;
  }, [contact, contactId, navigate]);

  const moreActions = useMemo(() => [
    {
      id: 'delete',
      label: 'Delete Contact',
      icon: '🗑️',
      onClick: () => { if (window.confirm('Are you sure you want to delete this contact?')) {} },
      variant: 'warning' as const
    }
  ], []);

  return (
    <DetailPageLayout
      title={fullName || 'Contact'}
      backLink="/crm#contacts"
      backLabel="Back to Contacts"
      actions={
        <>
          {contact?.company_id && (
            <Link 
              className="text-sm font-medium text-blue-600 hover:text-blue-700" 
              to={`/crm/companies/${encodeURIComponent(contact.company_id as string)}`}
            >
              View Company
            </Link>
          )}
          <button
            onClick={save}
            disabled={loading || !contact}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save'}
          </button>
        </>
      }
    >

        {duplicates.length > 0 && (
          <DuplicateAlert
            duplicates={duplicates}
            currentRecordId={contactId}
            onMerge={(duplicateId: string) => navigate(`/crm/contacts/merge?primary=${contactId}&duplicate=${duplicateId}`)}
            onDismiss={() => setDuplicates([])}
            onViewAll={() => navigate('/crm/data-quality')}
          />
        )}

        {contact && <QuickActionsBar actions={quickActionsBarActions} moreActions={moreActions} />}

      {insights.length > 0 && <SmartInsightsPanel insights={insights} loading={false} />}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Contact Information</h2>
            
            {error && <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 mb-4">{error}</div>}
            {loading && !contact && <div className="text-sm text-gray-600">Loading…</div>}

            {contact && (
              <>
                <div className="grid gap-6 md:grid-cols-2 mb-6">
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">Profile</h3>
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Job title" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Department" value={department} onChange={(e) => setDepartment(e.target.value)} />
                    <select className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                      <option value="">Company (optional)</option>
                      {companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
                    </select>
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="LinkedIn URL" value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} />
                    {linkedinUrl && <a className="inline-flex items-center text-sm text-blue-600 hover:text-blue-700" href={linkedinUrl} target="_blank" rel="noreferrer">💼 Open LinkedIn profile →</a>}
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">📱 Contact Methods</h3>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">📱 Mobile Phone (Primary)</label>
                      <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="+1 (555) 123-4567" type="tel" value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">☎️ Business Phone</label>
                        <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="+1 (555) 987-6543" type="tel" value={businessPhone} onChange={(e) => setBusinessPhone(e.target.value)} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Extension</label>
                        <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="1234" value={businessPhoneExt} onChange={(e) => setBusinessPhoneExt(e.target.value)} maxLength={10} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">🏠 Home Phone</label>
                      <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="+1 (555) 111-2222" type="tel" value={homePhone} onChange={(e) => setHomePhone(e.target.value)} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">📞 Other Phone</label>
                      <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="+1 (555) 333-4444" type="tel" value={otherPhone} onChange={(e) => setOtherPhone(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="grid gap-6 md:grid-cols-2 mb-6">
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">📍 Address</h3>
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Street address" value={addressStreet} onChange={(e) => setAddressStreet(e.target.value)} />
                    <div className="grid grid-cols-2 gap-2">
                      <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="City" value={addressCity} onChange={(e) => setAddressCity(e.target.value)} />
                      <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="State/Province" value={addressState} onChange={(e) => setAddressState(e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Postal code" value={addressPostalCode} onChange={(e) => setAddressPostalCode(e.target.value)} />
                      <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Country" value={addressCountry} onChange={(e) => setAddressCountry(e.target.value)} />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700">🎯 Lead & Segmentation</h3>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Lead Source</label>
                      <select className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" value={leadSource} onChange={(e) => setLeadSource(e.target.value)}>
                        <option value="">Select source...</option>
                        <option value="Website">Website</option>
                        <option value="Referral">Referral</option>
                        <option value="Social Media">Social Media</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Trade Show">Trade Show</option>
                        <option value="Conference">Conference</option>
                        <option value="Cold Call">Cold Call</option>
                        <option value="Email Campaign">Email Campaign</option>
                        <option value="Google Ads">Google Ads</option>
                        <option value="Partner">Partner</option>
                        <option value="Customer">Customer</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <select className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" value={leadStatus} onChange={(e) => setLeadStatus(e.target.value)}>
                      {leadStatusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <select className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" value={lifecycleStage} onChange={(e) => setLifecycleStage(e.target.value)}>
                      {lifecycleStageOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <select className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" value={persona} onChange={(e) => setPersona(e.target.value)}>
                      {personaOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    <input className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Region" value={region} onChange={(e) => setRegion(e.target.value)} />
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <h3 className="text-sm font-semibold text-gray-700">📝 Internal Notes</h3>
                  <textarea className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" placeholder="Add internal notes about this contact..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} />
                  <p className="text-xs text-gray-500">These notes are only visible to your team</p>
                </div>

                <div className="flex items-center justify-end pt-4 border-t border-gray-100">
                  <button className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed" onClick={save} disabled={loading}>
                    {loading ? 'Saving...' : 'Save changes'}
                  </button>
                </div>
              </>
            )}
          </section>

          {contact && (
            <>
              <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Tags</h2>
                <TagsManager tags={tags} availableTags={availableTags} onAddTag={handleAddTag} onRemoveTag={handleRemoveTag} onCreateTag={handleCreateTag} loading={loading} />
              </section>

              {/* Unified Activity Timeline - Shows CRM + Email activities */}
              <UnifiedActivityTimeline
                contactId={contactId}
                organizationId={activeOrgId || ''}
                entityType="contact"
                entityId={contactId}
                limit={50}
              />

              <section className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-100">
                <NotesPanel entityType="contact" entityId={contactId} title="Contact Notes" />
              </section>
            </>
          )}
        </div>

        <div className="space-y-6">
          {contact && leadScore && (
            <LeadScoreCard 
              score={leadScore.score} 
              grade={leadScore.grade} 
              trend="up" 
              lastCalculated={leadScore.calculated_at} 
              breakdown={scoreBreakdown.map(rule => ({
                rule: rule.name,
                points: rule.points,
                matched: rule.matched || false
              }))}
              onClick={() => navigate(`/crm/lead-scoring/contact/${contactId}`)} 
            />
          )}

          {/* Email Campaign Widget - Integrated email outreach */}
          {contact && activeOrgId && (
            <EmailCampaignWidget
              contactId={contactId}
              organizationId={activeOrgId}
              contactEmail={contact.email}
            />
          )}

          {contact && activeOrgId && (
            <AutomationManager
              contactId={contactId}
              organizationId={activeOrgId}
              currentEnrollments={workflowEnrollments}
              onEnrollmentChange={loadContactData}
            />
          )}
        </div>
      </div>
    </DetailPageLayout>
  );
};

export default ContactDetailEnhanced;
